import { z } from 'zod';

/**
 * Scalar building blocks shared by every schema in the package.
 *
 * These deliberately contain no transforms. Parsing a valid object must return
 * exactly what went in: the evidence pack has to be able to claim that what was
 * approved is byte-for-byte what was stored.
 */

/** ISO-8601, UTC only. Audit trails that mix offsets cannot be ordered safely. */
export const ISO_8601_UTC = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z$/;

export const TimestampSchema = z
  .string()
  .regex(ISO_8601_UTC, 'must be an ISO-8601 UTC timestamp ending in Z');
export type Timestamp = z.infer<typeof TimestampSchema>;

export const NonEmptyStringSchema = z.string().min(1);

/**
 * A question put to a client — the shape, not the wording
 * (docs/DECISIONS.md ZD-103).
 *
 * ## What this refuses, and why the rule is the voice rather than a word list
 *
 * `question_hint` is rendered to a client under a heading that calls its
 * contents questions. Measured across the corpus's four systems, **58 of 71**
 * canonical unknowns were not questions at all: they were explanations written
 * for a different reader, and stripping the internal references out of them
 * would have left the voice exactly as wrong. **0 of those 58 contained a
 * question mark; 13 of 13 written by production code did.**
 *
 * A blocklist of internal vocabulary — the instrument that first found this —
 * caught **5 of the 58**. That is the difference between guarding the symptom
 * and guarding the field: a term list can only ever name the leaks somebody has
 * already thought of, and the thing that makes a sentence unfit here is that it
 * is not an ask.
 *
 * ## Why containment rather than the stricter rule
 *
 * *Ends with a question mark* was considered and refused. It is stricter, and on
 * every input that exists the two are **indistinguishable** — 13 of 13 of the
 * production hints both contain one and end with one, and 0 of 58 do either. So
 * choosing the stricter rule would be picking, on no evidence, a rule that also
 * refuses a question followed by a legitimate qualifier (*"Is this flow
 * encrypted? Page 2 of the survey could not be read."*), which is a shape
 * nothing has shown to be wrong. ZD-052 §5's discipline: where nothing varies,
 * the stricter check is a claim about the author rather than about the data.
 *
 * The minimal rule that catches the measured defect is the rule to write, and a
 * later session with a population that separates them may tighten it.
 *
 * ## The one thing it cannot do
 *
 * It is a shape check, not a comprehension check. *"Is the thing the corpus
 * exists to catch present?"* would pass. What it guarantees is that a paragraph
 * of explanation cannot sit in a field a client reads as a question — which is
 * the whole of the defect that was actually shipped.
 */
export class NotAClientQuestionError extends Error {
  constructor(text: string) {
    super(
      `this field is rendered to a client under a heading that calls it a question, and this ` +
        `is not one: "${text}". A hint that explains why we are asking, to a reader who is not ` +
        `the client, is the defect — not the words it happens to use`,
    );
    this.name = 'NotAClientQuestionError';
  }
}

/**
 * One schema with two consumers — canonical `Unknown` and `ExtractedUnknown` —
 * rather than the same refinement written twice (ZD-017).
 *
 * The two agree on every input today and would go on agreeing, which is exactly
 * the condition under which two copies drift invisibly.
 */
export const ClientQuestionSchema = NonEmptyStringSchema.superRefine((text, ctx) => {
  if (text.includes('?')) return;
  ctx.addIssue({ code: 'custom', message: new NotAClientQuestionError(text).message });
});
export const Sha256Schema = z
  .string()
  .regex(/^[0-9a-f]{64}$/, 'must be a lowercase hex SHA-256 digest');
export type Sha256 = z.infer<typeof Sha256Schema>;

export const SemVerSchema = z
  .string()
  .regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/, 'must be a semantic version');
export type SemVer = z.infer<typeof SemVerSchema>;

/**
 * Three-valued logic. `'unknown'` is a first-class answer, never coerced to
 * `false`: DESIGN_SPEC §7.5 requires the canonical model to preserve
 * uncertainty rather than silently discard it, and §8.4 derives a finding's
 * confidence from precisely this distinction.
 */
export const TristateSchema = z.union([z.boolean(), z.literal('unknown')]);
export type Tristate = z.infer<typeof TristateSchema>;

/** Free-form structured detail (audit payloads, scoring sub-objects). */
export const DetailBagSchema = z.record(z.string(), z.unknown());
export type DetailBag = z.infer<typeof DetailBagSchema>;

/** Returns the current time in the only timestamp format this codebase accepts. */
export function nowIso(): Timestamp {
  return new Date().toISOString();
}

/** Injectable clock, so tests and replays can be deterministic. */
export type Clock = () => Timestamp;
